import { useMemo, useSyncExternalStore } from "react";
import { getLocale, LANGUAGES, setLocale, subscribe, type LocaleCode } from "./lang-router.js";
import { formatDate, formatDateTime, formatNumber, translate, translateNodes, translateOr, translatePlural } from "./translate.js";
import type { Language } from "./types.js";

/**
 * React access to the language router.
 *
 * There is no provider: the current language lives in the router (loaders and
 * actions need it too), and this hook subscribes the component tree to it, so
 * switching the language re-renders every page that translates something.
 */
export interface I18n {
  /** Active language code, e.g. `zh-CN`. */
  readonly locale: LocaleCode;
  /** Active language descriptor (tag, native label, direction). */
  readonly language: Language;
  /** Every language this app ships, in menu order. */
  readonly languages: typeof LANGUAGES;
  readonly setLocale: (code: LocaleCode) => void;
  /** Translate a known key. */
  readonly t: typeof translate;
  /** Translate a key built from data, falling back to the raw value. */
  readonly tOr: typeof translateOr;
  /** Translate a message whose placeholders are React nodes. */
  readonly tNode: typeof translateNodes;
  /** Translate a count-dependent message. */
  readonly tPlural: typeof translatePlural;
  readonly formatDate: typeof formatDate;
  readonly formatDateTime: typeof formatDateTime;
  readonly formatNumber: typeof formatNumber;
}

export function useI18n(): I18n {
  const locale = useSyncExternalStore(subscribe, getLocale, getLocale);
  return useMemo<I18n>(
    () => ({
      locale,
      language: LANGUAGES.find((l) => l.code === locale) ?? LANGUAGES[0],
      languages: LANGUAGES,
      setLocale,
      t: translate,
      tOr: translateOr,
      tNode: translateNodes,
      tPlural: translatePlural,
      formatDate,
      formatDateTime,
      formatNumber,
    }),
    [locale],
  );
}

/** Shorthand for the common case: `const t = useT()`. */
export function useT(): I18n["t"] {
  return useI18n().t;
}
